import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { apiCallWithHost } from '../../utils/apiClient'

// Async thunk for fetching user data
export const fetchUserData = createAsyncThunk(
  'user/fetchUserData',
  async (token, { getState, rejectWithValue }) => {
    const state = getState()
    const lastFetched = state.user.lastFetched
    const now = Date.now()

    // If we've fetched in the last 5 seconds, don't fetch again
    if (lastFetched && now - lastFetched < 5000) {
      return state.user.user
    }

    try {
      const response = await apiCallWithHost(`/users`, {}, token)

      if (!response.ok) {
        if (response.status === 500) {
          throw new Error('Server error - please try again later')
        } else if (response.status === 404) {
          // User doesn't exist yet - this is normal for new users
          // Return an empty user object with no profiles to indicate new user needs onboarding
          console.log('User not found (404) - new user needs onboarding')
          return {
            email: null,
            profiles: [], // Empty profiles array = needs onboarding
            sourceSystem: 'OAUTH',
          }
        } else {
          throw new Error(`Failed to fetch user data (${response.status})`)
        }
      }

      const userData = await response.json()
      return userData
    } catch (error) {
      console.error('Error fetching user data:', error)
      return rejectWithValue(error.message)
    }
  },
)

const initialState = {
  user: null,
  isLoading: false,
  error: null,
  trainerCode: '',
  clientCode: '',
  trainerData: null, // Data from /trainers endpoint
  clientData: null, // Data from /clients endpoint
  trainerProfile: null, // Data from /profiles endpoint
  clientProfile: null, // Data from /profiles endpoint
  activeProfile: null, // Currently active profile (trainer or client)
  lastFetched: null, // Timestamp of last successful fetch
  onboardingComplete: false, // Whether user has completed onboarding (has profiles)
  debugMode: (() => {
    try {
      const saved = localStorage.getItem('debugMode')
      return saved ? JSON.parse(saved) : false
    } catch {
      return false
    }
  })(), // Debug mode toggle for development features
}

const userSlice = createSlice({
  name: 'user',
  initialState: initialState,
  reducers: {
    setUser: (state, action) => {
      state.user = action.payload
      state.error = null
    },
    setLoading: (state, action) => {
      state.isLoading = action.payload
    },
    setError: (state, action) => {
      state.error = action.payload
      state.user = null
    },
    clearUser: (state) => {
      state.user = null
      state.error = null
      state.lastFetched = null
      state.trainerCode = ''
      state.clientCode = ''
      state.trainerData = null
      state.clientData = null
      state.trainerProfile = null
      state.clientProfile = null
      state.activeProfile = null
      state.onboardingComplete = false
      // localStorage clearing is now handled by localStorageMiddleware
    },
    setTrainerCode: (state, action) => {
      state.trainerCode = action.payload
    },
    setClientCode: (state, action) => {
      state.clientCode = action.payload
    },
    setTrainerData: (state, action) => {
      state.trainerData = action.payload
    },
    setClientData: (state, action) => {
      state.clientData = action.payload
    },
    setTrainerProfile: (state, action) => {
      state.trainerProfile = action.payload
    },
    setClientProfile: (state, action) => {
      state.clientProfile = action.payload
    },
    setActiveProfile: (state, action) => {
      state.activeProfile = action.payload
      // Store in localStorage for persistence
      localStorage.setItem('activeProfile', JSON.stringify(action.payload))
    },
    setDebugMode: (state, action) => {
      state.debugMode = action.payload
      // Store in localStorage for persistence
      localStorage.setItem('debugMode', JSON.stringify(action.payload))
    },
    setOnboardingComplete: (state, action) => {
      state.onboardingComplete = action.payload
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUserData.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(fetchUserData.fulfilled, (state, action) => {
        state.isLoading = false
        state.user = action.payload
        state.lastFetched = Date.now()
        state.error = null // Clear any previous errors

        // Update profiles if they exist
        if (action.payload && action.payload.profiles) {
          const trainerProfile = action.payload.profiles.find(
            (profile) => profile.role === 'TRAINER',
          )
          const clientProfile = action.payload.profiles.find((profile) => profile.role === 'CLIENT')

          if (trainerProfile) {
            state.trainerProfile = trainerProfile
          }
          if (clientProfile) {
            state.clientProfile = clientProfile
          }

          // Set onboarding as complete if user has any profiles
          if (action.payload.profiles.length > 0) {
            state.onboardingComplete = true

            // Set active profile if none is set or if current active profile is invalid
            const currentActiveProfileId = state.activeProfile?.id
            const validProfiles = action.payload.profiles
            const currentProfileStillValid = validProfiles.find(
              (p) => p.id === currentActiveProfileId,
            )

            if (!state.activeProfile || !currentProfileStillValid) {
              // Prefer trainer profile, fallback to client profile
              const defaultProfile = trainerProfile || clientProfile
              if (defaultProfile) {
                state.activeProfile = defaultProfile
                // Store in localStorage for persistence
                localStorage.setItem('activeProfile', JSON.stringify(defaultProfile))
              }
            }
          }
        } else {
          // No profiles means onboarding is not complete
          state.onboardingComplete = false
        }
      })
      .addCase(fetchUserData.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload
      })
  },
})

// Selectors
export const getTrainerProfile = (state) => state.user.trainerProfile
export const getClientProfile = (state) => state.user.clientProfile
export const getTrainerData = (state) => state.user.trainerData
export const getClientData = (state) => state.user.clientData
export const getTrainerCode = (state) => state.user.trainerCode
export const getClientCode = (state) => state.user.clientCode
export const getActiveProfile = (state) => state.user.activeProfile
export const getUserData = (state) => state.user.user
export const getUserLoading = (state) => state.user.isLoading
export const getUserError = (state) => state.user.error
export const getLastFetched = (state) => state.user.lastFetched
export const getDebugMode = (state) => state.user.debugMode
export const getOnboardingComplete = (state) => state.user.onboardingComplete

export const {
  setUser,
  setLoading,
  setError,
  clearUser,
  setTrainerCode,
  setClientCode,
  setTrainerData,
  setClientData,
  setTrainerProfile,
  setClientProfile,
  setActiveProfile,
  setDebugMode,
  setOnboardingComplete,
} = userSlice.actions

export default userSlice.reducer
